import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { uploadsBaseUrl } from '../../api';
import { useAuth } from '../../context/AuthContext';
import TopBar from '../../Components/TopBar';
import Navbar from '../../Components/Navbar';
import Footer from '../../Components/Footer';
import './CourseDetailsPage.css';

const formatDate = value => {
  if (!value) return 'TBA';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString();
};

const isEnrollIntake = intake => {
  if (!intake || intake.status !== 'open') return false;
  if (!intake.end_date) return true;
  const end = new Date(intake.end_date);
  const today = new Date();
  end.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return end >= today;
};

const CourseDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();

  const [course, setCourse] = useState(null);
  const [courseIntakeId, setCourseIntakeId] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentProof, setPaymentProof] = useState(null);

  const fullName = user?.fullName || user?.name || '';
  const email = user?.email || '';
  const selectedIntakeFromQuery = new URLSearchParams(location.search).get('intake');
  const redirectPath = `/courses/${id}?enroll=1${courseIntakeId ? `&intake=${courseIntakeId}` : ''}`;

  useEffect(() => {
    if (user?.phone && !phone) {
      setPhone(user.phone);
    }
  }, [user, phone]);

  useEffect(() => {
    loadCourse();
  }, [id]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get('enroll') === '1') {
      setTimeout(() => {
        const el = document.getElementById('course-enroll-form');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
    }
  }, [location.search, course]);

  const loadCourse = async () => {
    const res = await api.get(`/courses/${id}`);
    setCourse(res.data);
    const openIntakes = (res.data?.intakes || []).filter(isEnrollIntake);
    if (!openIntakes.length) return;

    const queryIntake = selectedIntakeFromQuery
      ? openIntakes.find(i => String(i.id) === String(selectedIntakeFromQuery))
      : null;

    setCourseIntakeId(String((queryIntake || openIntakes[0]).id));
  };

  const enroll = async e => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.info('Please login or create an account to enroll');
      navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`);
      return;
    }

    if (!paymentProof) {
      toast.error('Payment proof is required');
      return;
    }

    if (!courseIntakeId) {
      toast.error('Please select an intake/session');
      return;
    }

    const form = new FormData();
    form.append('course_id', id);
    form.append('course_intake_id', courseIntakeId);
    form.append('phone_number', phone);
    form.append('additional_notes', notes);
    form.append('payment_proof', paymentProof);

    try {
      await api.post('/enrollments', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Enrollment submitted successfully');
      navigate('/student/dashboard');
    } catch (err) {
      const validationErrors = err.response?.data?.errors;
      const firstValidationError = validationErrors
        ? Object.values(validationErrors)?.[0]?.[0]
        : null;
      const msg = firstValidationError || err.response?.data?.message || 'Enrollment failed';
      toast.error(msg);
    }
  };

  if (!course) return <div className="p-5">Loading...</div>;

  const availableIntakes = (course.intakes || []).filter(isEnrollIntake);
  const hasSelectedIntakeFromList = availableIntakes.some(i => String(i.id) === String(courseIntakeId));
  const isIntakeLocked = Boolean(selectedIntakeFromQuery && hasSelectedIntakeFromList);
  const selectedIntake = availableIntakes.find(i => String(i.id) === String(courseIntakeId));

  return (
    <>
      <TopBar />
      <main className="page-about">
        <div className="container">
          <Navbar />
          <div className="py-4 lms-course-details-wrap">
            <section className="lms-course-details-hero mb-4">
              <div>
                <span className="lms-course-details-chip">{course.category || 'Professional Course'}</span>
                <h2 className="mt-2 mb-2">{course.title}</h2>
                <p className="mb-0 lms-course-details-subtitle">{course.description}</p>
              </div>
            </section>

            <div className="row g-4">
              <div className="col-lg-7">
                <section className="lms-course-details-card mb-4">
                  <h5 className="mb-3">Course information</h5>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <p className="lms-course-details-label">Trainer</p>
                      <p className="mb-0">{course.trainer || 'To be announced'}</p>
                    </div>
                    <div className="col-md-6">
                      <p className="lms-course-details-label">Duration</p>
                      <p className="mb-0">{course.duration || 'TBA'}</p>
                    </div>
                    <div className="col-md-6">
                      <p className="lms-course-details-label">Schedule</p>
                      <p className="mb-0">{course.schedule || 'TBA'}</p>
                    </div>
                    <div className="col-md-6">
                      <p className="lms-course-details-label">Tuition fee</p>
                      <p className="mb-0 fw-semibold">
                        {course.fee_currency || 'RWF'} {course.fee}
                      </p>
                    </div>
                  </div>
                </section>

                <section className="lms-course-details-card">
                  <h5 className="mb-3">What you will learn</h5>
                  {(course.outline || []).length ? (
                    <ul className="lms-course-outline">
                      {(course.outline || []).map((item, i) => (
                        <li key={`${item}-${i}`}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted mb-0">Detailed outline will be shared before class starts.</p>
                  )}
                </section>
              </div>

              <div className="col-lg-5">
                <section className="lms-course-enroll-card" id="course-enroll-form">
                  <h5 className="mb-2">Enroll now</h5>
                  <p className="text-muted small mb-3">Secure your seat by completing the form below.</p>

                  <form className="row g-3" onSubmit={enroll}>
                    <div className="col-md-6">
                      <label className="form-label">Full name</label>
                      <input
                        className="form-control"
                        value={fullName}
                        placeholder="Full name"
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Email</label>
                      <input className="form-control" value={email} placeholder="Email" readOnly />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Intake / Session</label>
                      {isIntakeLocked && selectedIntake ? (
                        <input
                          className="form-control"
                          value={`${selectedIntake.name} (${formatDate(selectedIntake.start_date)} to ${formatDate(selectedIntake.end_date)})`}
                          readOnly
                        />
                      ) : (
                        <select
                          required
                          className="form-select"
                          value={courseIntakeId}
                          onChange={e => setCourseIntakeId(e.target.value)}
                        >
                          <option value="">Select intake/session</option>
                          {availableIntakes.map(i => (
                            <option key={i.id} value={i.id}>
                              {i.name}
                              {i.start_date || i.end_date
                                ? ` (${formatDate(i.start_date)} to ${formatDate(i.end_date)})`
                                : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="col-12">
                      <label className="form-label">Phone number</label>
                      <input
                        required
                        className="form-control"
                        placeholder="Phone number"
                        value={phone}
                        onChange={e => setPhone(e.target.value)}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Additional notes</label>
                      <textarea
                        className="form-control"
                        placeholder="Tell us anything relevant for your enrollment"
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        rows={4}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Payment proof</label>
                      <input
                        type="file"
                        required
                        accept=".pdf,.png,.jpg,.jpeg"
                        className="form-control"
                        onChange={e => setPaymentProof(e.target.files?.[0])}
                      />
                      <small className="text-muted">Accepted: PDF, PNG, JPG (max 5MB)</small>
                    </div>
                    <div className="col-12 d-flex flex-wrap gap-2">
                      <button className="btn btn-primary" type="submit">
                        Submit enrollment
                      </button>
                      {!isAuthenticated && (
                        <>
                          <Link
                            to={`/login?redirect=${encodeURIComponent(redirectPath)}`}
                            className="btn btn-outline-primary"
                          >
                            Login to enroll
                          </Link>
                          <Link
                            to={`/register?redirect=${encodeURIComponent(redirectPath)}`}
                            className="btn btn-outline-secondary"
                          >
                            Create account
                          </Link>
                        </>
                      )}
                      <Link to={uploadsBaseUrl} className="btn btn-outline-secondary d-none">
                        hidden
                      </Link>
                    </div>
                  </form>
                </section>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    </>
  );
};

export default CourseDetailsPage;
